/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Sidebar, tituloBase, usePermiso } from "../../shared";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Card } from "primereact/card";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { Divider } from "primereact/divider";
import { Skeleton } from "primereact/skeleton";
import { Column } from "primereact/column";
import {
  accessAPI,
  downloadFile,
  formatFecha,
  Titulo,
  Boton,
  Formulario,
  CampoInformacion,
  InfoCertificadoPropuesta,
  TablaFiltrable,
  ArchivoPreview
} from "../../shared";
import { useContextoAsistente } from "../asistente/ContextoAsistente";

const FRECUENCIA_OPCIONES = [
  { value: "NINGUNA", etiqueta: "Por actividad" },
  { value: "SEMANAL", etiqueta: "Semanal" },
  { value: "MENSUAL", etiqueta: "Mensual" },
  { value: "SEMESTRAL", etiqueta: "Semestral" },
  { value: "ANUAL", etiqueta: "Anual" },
  { value: "BIENAL", etiqueta: "Bienal" },
  { value: "TRIENAL", etiqueta: "Trienal" }
];
const frecuenciaEtiqueta = (v) =>
  FRECUENCIA_OPCIONES.find((f) => f.value === (v || "NINGUNA"))?.etiqueta ||
  "Por actividad";

export default function PropuestaComercial() {
  // Con lectura de facturación la propuesta se ve y se descarga, sin poder
  // editarla, enviarla, archivarla, retirarla ni eliminarla
  const { puedeEditar } = usePermiso("admin.facturacion");
  const [loader, setLoader] = useState(true);
  const [propuesta, setPropuesta] = useState();
  // Los precios y adicionales se editan solo antes de emitir y con full
  const soloLectura = !!propuesta?.fechaemision || !puedeEditar;
  const [precios, setPrecios] = useState({});
  const [actualizandoPrecios, setActualizandoPrecios] = useState(false);
  const [enviandoPropuesta, setEnviandoPropuesta] = useState(false);
  const [monedas, setMonedas] = useState([]);
  const [adicionales, setAdicionales] = useState([]);
  const [resetForm, setResetForm] = useState(1);

  const [loaderDescargarEnPDF, setLoaderDescargarEnPDF] = useState(false);
  const [sidebarEnviar, setSidebarEnviar] = useState(false);
  const [sidebarArchivar, setSidebarArchivar] = useState(false);
  const [loaderArchivar, setLoaderArchivar] = useState(false);
  const [loaderEliminar, setLoaderEliminar] = useState(false);
  const [loaderRetirar, setLoaderRetirar] = useState(false);
  const [masIva, setMasIva] = useState(false);
  const [loaderMasIva, setLoaderMasIva] = useState(false);

  // Estado para el formulario de adicionales
  const [nuevoAdicionalDescripcion, setNuevoAdicionalDescripcion] =
    useState("");
  const [nuevoAdicionalPrecio, setNuevoAdicionalPrecio] = useState(null);
  const [nuevoAdicionalMoneda, setNuevoAdicionalMoneda] = useState(null);
  const [nuevoAdicionalModifica, setNuevoAdicionalModifica] = useState(false);

  const toast = useRef(null);
  const isInitialMount = useRef(true);
  const debounceTimer = useRef(null);
  const skipNextSave = useRef(false);

  // Necesito estos refs porque el callback del formulario se queda con el state inicial en la definición
  const preciosActualizadosRef = useRef();
  preciosActualizadosRef.current = precios;
  const adicionalesActualizadosRef = useRef();
  adicionalesActualizadosRef.current = adicionales;

  // Carga el id de la propuesta de la URL
  const { propuestaId } = useParams();
  useContextoAsistente({
    tipo: "propuesta",
    id: propuestaId,
    nombre: propuesta?.solicitud?.nombrefantasia
  });
  const navigate = useNavigate();

  // (Re)carga la propuesta desde el backend. Al traer objetos nuevos, la grilla
  // de PrimeReact (que memoiza filas por referencia de rowData) se re-renderiza,
  // por eso se usa tras retirar para reflejar el estado editable sin recargar.
  const cargarPropuesta = () => {
    accessAPI(
      "GET",
      `admin/propuesta/${propuestaId}`,
      null,
      (respuesta) => {
        setPropuesta(respuesta);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar propuesta",
          sticky: true
        });
      }
    );
  };

  // Cuando carga el componente va a buscar la propuesta y las monedas
  useEffect(() => {
    cargarPropuesta();

    // Cargar monedas
    accessAPI(
      "GET",
      "admin/config/moneda?dropdown=true",
      null,
      (respuesta) => {
        setMonedas(respuesta);
        document.title = `Propuesta comercial ${respuesta?.solicitud?.nombrefantasia}`;
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Error al cargar monedas: " + respuesta[0]?.msg,
          sticky: true
        });
      }
    );
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cuando carga la propuesta apaga el loader y popula el state precios
  useEffect(() => {
    if (propuesta) {
      setLoader(false);
      setMasIva(propuesta.masiva || false);
      // Popular el state precios con el objeto precios completo
      if (propuesta.precios) {
        setPrecios(propuesta.precios);
        // Cargar adicionales existentes
        if (propuesta.precios.adicionales) {
          setAdicionales(propuesta.precios.adicionales);
        }
      }
      // Marcar que ya pasó el mount inicial después de un pequeño delay
      setTimeout(() => {
        isInitialMount.current = false;
      }, 100);
    }
  }, [propuesta]);

  // Cuando cargan las monedas, setear la primera como default
  useEffect(() => {
    if (monedas.length > 0 && !nuevoAdicionalMoneda) {
      setNuevoAdicionalMoneda(monedas[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monedas]);

  // Función para cambiar el estado de "más IVA"
  const toggleMasIva = (nuevoValor) => {
    setLoaderMasIva(true);
    accessAPI(
      "PUT",
      `admin/propuesta/${propuesta.id}`,
      { masiva: nuevoValor },
      () => {
        setMasIva(nuevoValor);
        setLoaderMasIva(false);
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: "Propuesta actualizada"
        });
      },
      (respuesta) => {
        setLoaderMasIva(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar propuesta",
          sticky: true
        });
      }
    );
  };

  // Las actividades se identifican por precioActividadClienteId (id de la fila).
  // `patch` puede ser un objeto o una función que recibe la fila actual.
  const mapActividades = (id, patch) => (prev) => ({
    ...prev,
    actividades:
      prev.actividades?.map((a) =>
        a.precioActividadClienteId === id
          ? { ...a, ...(typeof patch === "function" ? patch(a) : patch) }
          : a
      ) || []
  });

  const handlePrecioChange = (id, nuevoPrecio) => {
    // Recalcula también el subtotal de la primera facturación (precio × cantidad)
    setPrecios(
      mapActividades(id, (a) => ({
        precio: nuevoPrecio,
        subtotal: (nuevoPrecio || 0) * (a.cantidad || 0)
      }))
    );
  };

  const handleMonedaChange = (id, nuevaMonedaId) => {
    const m = monedas.find((mm) => mm.value === nuevaMonedaId);
    setPrecios(
      mapActividades(id, {
        monedaid: parseInt(nuevaMonedaId),
        moneda: m
          ? {
              id: parseInt(nuevaMonedaId),
              nombre: m.etiqueta,
              simbolo: m.simbolo || ""
            }
          : null
      })
    );
  };

  const handleFrecuenciaChange = (id, frecuencia) => {
    setPrecios(
      mapActividades(id, {
        frecuenciaRefacturacion: frecuencia === "NINGUNA" ? null : frecuencia
      })
    );
  };

  // Marca si la actividad se crea en los procesos al aceptar la propuesta: una
  // por proceso del tipo de certificado, que es lo que se factura primero.
  const handleCrearAutomaticamenteChange = (id, crearAutomaticamente) => {
    setPrecios(
      mapActividades(id, (a) => {
        const cantidad =
          crearAutomaticamente && !a.excluida ? a.procesosCertificado || 0 : 0;
        return {
          crearAutomaticamente,
          cantidad,
          subtotal: (a.precio || 0) * cantidad
        };
      })
    );
  };

  // Saca una actividad de la propuesta: queda tachada para poder reactivarla
  // y se borra al emitir (el auto-save por PUT /precios persiste el cambio).
  const handleEliminarActividad = (id) => {
    setPrecios(mapActividades(id, { excluida: true, cantidad: 0, subtotal: 0 }));
  };

  const handleReactivarActividad = (id) => {
    setPrecios(
      mapActividades(id, (a) => {
        const cantidad = a.crearAutomaticamente ? a.procesosCertificado || 0 : 0;
        return {
          excluida: false,
          cantidad,
          subtotal: (a.precio || 0) * cantidad
        };
      })
    );
  };

  // Función para agregar adicional
  const agregarAdicional = () => {
    if (
      !nuevoAdicionalDescripcion ||
      !nuevoAdicionalPrecio ||
      !nuevoAdicionalMoneda
    ) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Complete todos los campos del adicional",
        sticky: true
      });
      return;
    }

    const monedaSeleccionada = monedas.find(
      (m) => m.value === nuevoAdicionalMoneda
    );
    const adicional = {
      descripcion: nuevoAdicionalDescripcion,
      precio: nuevoAdicionalPrecio,
      modifica: nuevoAdicionalModifica,
      monedaid: nuevoAdicionalMoneda,
      moneda: monedaSeleccionada
        ? {
            id: nuevoAdicionalMoneda,
            nombre: monedaSeleccionada.etiqueta,
            simbolo: monedaSeleccionada.simbolo || monedaSeleccionada.etiqueta
          }
        : null
    };

    setAdicionales((prev) => [...prev, adicional]);

    // Limpiar formulario
    setNuevoAdicionalDescripcion("");
    setNuevoAdicionalPrecio(null);
    setNuevoAdicionalModifica(false);
  };

  // Función para eliminar adicional
  const eliminarAdicional = (index) => {
    setAdicionales((prev) => prev.filter((_, i) => i !== index));
  };

  // Función para actualizar precios en la API
  const actualizarPrecios = (preciosData, adicionalesData) => {
    setActualizandoPrecios(true);
    const preciosCompletos = {
      actividades: preciosData.actividades || [],
      adicionales: adicionalesData,
      totales: preciosData.totales || []
    };
    accessAPI(
      "PUT",
      `admin/propuesta/${propuesta.id}/precios`,
      { precios: preciosCompletos },
      (respuesta) => {
        setActualizandoPrecios(false);
        // Actualizar totales con los datos devueltos por la API
        if (respuesta.precios?.totales) {
          skipNextSave.current = true;
          setPrecios((prev) => ({
            ...prev,
            totales: respuesta.precios.totales
          }));
        }
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar precios",
          sticky: true
        });
        setActualizandoPrecios(false);
      }
    );
  };

  // Auto-guardar precios con debounce
  useEffect(() => {
    // Evitar guardar en el primer render o cuando no hay propuesta
    if (isInitialMount.current || !propuesta?.id || propuesta.fechaemision || !puedeEditar) {
      return;
    }

    // Si debemos saltar este guardado (porque viene de una respuesta de API)
    if (skipNextSave.current) {
      skipNextSave.current = false;
      return;
    }

    // Limpiar timer anterior
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Crear nuevo timer con delay de 800ms
    debounceTimer.current = setTimeout(() => {
      actualizarPrecios(precios, adicionales);
    }, 800);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [precios, adicionales]);

  // Función para enviar propuesta al cliente
  const enviarPropuesta = (camposFormulario) => {
    // El precio 0 es válido (actividad bonificada). Sólo se bloquea si falta
    // definir el precio (vacío): hay que cargarlo, escribir 0 para bonificar, o
    // eliminar la actividad de la propuesta.
    const actividadesPropuesta =
      preciosActualizadosRef.current.actividades || [];
    if (actividadesPropuesta.some((a) => !a.excluida && a.precio == null)) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail:
          "Hay actividades sin precio. Cargue el precio, escriba 0 para bonificar, o elimine las que no formarán parte de la propuesta.",
        sticky: true
      });
      return;
    }

    setEnviandoPropuesta(true);
    camposFormulario.precios = {
      actividades: preciosActualizadosRef.current.actividades || [],
      adicionales: adicionalesActualizadosRef.current,
      totales: preciosActualizadosRef.current.totales || []
    };
    accessAPI(
      "POST",
      `admin/propuesta/${propuesta.id}/enviar`,
      camposFormulario,
      (respuesta) => {
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: "Propuesta enviada al cliente correctamente"
        });
        setEnviandoPropuesta(false);
        setSidebarEnviar(false);
        setResetForm((current) => current + 1);
        // Actualizar la propuesta con los datos devueltos por la API
        if (respuesta) {
          setPropuesta(respuesta);
          if (respuesta.precios) {
            setPrecios(respuesta.precios);
            if (respuesta.precios.adicionales) {
              setAdicionales(respuesta.precios.adicionales);
            }
          }
        }
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al enviar propuesta",
          sticky: true
        });
        setEnviandoPropuesta(false);
      }
    );
  };

  const descargarEnPDF = () => {
    setLoaderDescargarEnPDF(true);
    downloadFile(
      `admin/propuesta/${propuesta.id}/pdf`,
      "Propuesta comercial",
      (respuesta) => {
        setLoaderDescargarEnPDF(false);
      },
      (respuesta) => {
        setLoaderDescargarEnPDF(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0]?.msg ||
            "Ocurrió un error, intenta nuevamente más tarde.",
          sticky: true
        });
      }
    );
  };

  const archivarPropuesta = (camposFormulario) => {
    setLoaderArchivar(true);
    accessAPI(
      "PUT",
      `admin/propuesta/${propuesta.id}/archivar`,
      camposFormulario || {},
      (respuesta) => {
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta[0]?.msg
        });
        setLoaderArchivar(false);
        setSidebarArchivar(false);
        setPropuesta((prev) => ({
          ...prev,
          archivada: !prev.archivada,
          notasArchivada: prev.archivada ? null : camposFormulario?.notasArchivada || null
        }));
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al archivar propuesta",
          sticky: true
        });
        setLoaderArchivar(false);
      }
    );
  };

  // Retira una propuesta emitida (aún no aceptada): la vuelve editable borrando la
  // fecha de emisión. Actualiza el state local para reactivar la edición en la UI.
  const retirarPropuesta = () => {
    setLoaderRetirar(true);
    accessAPI(
      "PUT",
      `admin/propuesta/${propuesta.id}/retirar`,
      {},
      (respuesta) => {
        setLoaderRetirar(false);
        toast.current?.show({
          severity: "success",
          summary: "Retirada",
          detail: respuesta[0]?.msg
        });
        // Re-fetch para reflejar el estado editable (la grilla necesita rowData
        // nuevo). Se marca como carga inicial para que la repoblación de precios
        // no dispare un autosave.
        isInitialMount.current = true;
        cargarPropuesta();
      },
      (respuesta) => {
        setLoaderRetirar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al retirar la propuesta",
          sticky: true
        });
      }
    );
  };

  const confirmarRetirar = () => {
    confirmDialog({
      message:
        "¿Retirar la propuesta? Volverá a ser editable y deberás emitirla nuevamente para que el cliente pueda aceptarla.",
      header: "Retirar propuesta",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Retirar",
      rejectLabel: "Cancelar",
      accept: retirarPropuesta
    });
  };

  // Elimina definitivamente una propuesta no emitida y vuelve al listado.
  const eliminarPropuesta = () => {
    setLoaderEliminar(true);
    accessAPI(
      "DELETE",
      `admin/propuesta/${propuesta.id}`,
      null,
      (respuesta) => {
        setLoaderEliminar(false);
        toast.current?.show({
          severity: "success",
          summary: "Eliminada",
          detail: respuesta[0]?.msg
        });
        // Vuelve a la solicitud que generó la propuesta si la tiene; si no, al
        // listado de procesos del cliente.
        navigate(
          propuesta.solicitud?.id
            ? `/procesos/solicitudes/${propuesta.solicitud.id}`
            : `/clientes/${propuesta.cliente?.id}/procesos`
        );
      },
      (respuesta) => {
        setLoaderEliminar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al eliminar la propuesta",
          sticky: true
        });
      }
    );
  };

  const confirmarEliminar = () => {
    confirmDialog({
      message:
        "¿Eliminar esta propuesta comercial? Se borrará por completo y no se puede deshacer.",
      header: "Confirmar eliminación",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Eliminar",
      rejectLabel: "Cancelar",
      acceptClassName: "p-button-danger",
      accept: eliminarPropuesta
    });
  };

  const archivarFormConfig = {
    secciones: [
      {
        descripcion:
          "La propuesta archivada no será visible para los clientes en ninguna sección.",
        campos: [
          {
            etiqueta: "Notas",
            nombre: "notasArchivada",
            tipoitemformularioid: 11,
            obligatorio: false,
            respuesta: ""
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Archivar propuesta",
        rojo: true,
        callback: archivarPropuesta,
        confirmacion: "¿Está seguro que desea archivar esta propuesta?"
      }
    ]
  };

  const enviarPropuestaFormConfig = {
    secciones: [
      {
        campos: [
          {
            etiqueta: "Notas para el cliente",
            nombre: "notas",
            tipoitemformularioid: 11,
            obligatorio: false,
            respuesta: propuesta?.notas || ""
          },
          {
            etiqueta: "Notas internas - no serán mostradas al cliente",
            nombre: "notasInternas",
            tipoitemformularioid: 11,
            obligatorio: false,
            respuesta: propuesta?.notasInternas || ""
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Enviar propuesta",
        verde: true,
        callback: enviarPropuesta,
        confirmacion: "¿Está seguro?"
      }
    ]
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />
      {loader && (
        <div>
          {/* Skeleton del Titulo */}
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem"
            }}
          >
            <Skeleton width="300px" height="2rem" />
            <div css={{ display: "flex", gap: "0.5rem" }}>
              <Skeleton width="120px" height="2.5rem" borderRadius="6px" />
              <Skeleton width="120px" height="2.5rem" borderRadius="6px" />
            </div>
          </div>

          {/* Skeleton de Card Detalles */}
          <Card>
            <Skeleton
              width="200px"
              height="1.5rem"
              css={{ marginBottom: "1rem" }}
            />
            <div className="grid">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="col-12 md:col-6 lg:col-4"
                  css={{ marginBottom: "1rem" }}
                >
                  <Skeleton
                    width="80px"
                    height="0.875rem"
                    css={{ marginBottom: "0.5rem" }}
                  />
                  <Skeleton width="150px" height="1.25rem" />
                </div>
              ))}
            </div>
          </Card>

          {/* Skeleton de Card Precios */}
          <Card css={{ marginTop: "1rem" }}>
            <Skeleton
              width="100px"
              height="1.5rem"
              css={{ marginBottom: "1rem" }}
            />
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                css={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0.75rem 0",
                  borderBottom: "1px solid var(--surface-200)"
                }}
              >
                <div>
                  <Skeleton
                    width="200px"
                    height="1rem"
                    css={{ marginBottom: "0.5rem" }}
                  />
                  <Skeleton width="120px" height="0.875rem" />
                </div>
                <div css={{ display: "flex", gap: "1rem" }}>
                  <Skeleton width="100px" height="2.5rem" borderRadius="6px" />
                  <Skeleton width="180px" height="2.5rem" borderRadius="6px" />
                </div>
              </div>
            ))}
            <Divider />
            <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <Skeleton width="60px" height="1rem" />
              <Skeleton width="100px" height="1rem" />
            </div>
          </Card>
        </div>
      )}
      {!loader && (
        <div>
          <Titulo
            urlVolver={`/clientes/${propuesta.cliente?.id}/procesos`}
            etiquetas={
              propuesta.archivada
                ? [{ texto: "Archivada", error: true }]
                : [
                    {
                      texto: propuesta.fechaaceptacion
                        ? "Aceptada"
                        : propuesta.fechaemision
                          ? "Enviada"
                          : "Pendiente",
                      ok: !!propuesta.fechaaceptacion,
                      alerta: !!(propuesta.fechaemision && !propuesta.fechaaceptacion),
                      neutro: !propuesta.fechaemision
                    }
                  ]
            }
            botones={
              <>
                <Boton
                  transparente
                  callback={descargarEnPDF}
                  loader={loaderDescargarEnPDF}
                >
                  Descargar en PDF
                </Boton>
                {!puedeEditar ? null : propuesta.archivada ? (
                  <Boton
                    rojo
                    callback={() => archivarPropuesta()}
                    loader={loaderArchivar}
                  >
                    Desarchivar
                  </Boton>
                ) : !propuesta.fechaaceptacion ? (
                  <Boton
                    rojo
                    callback={() => setSidebarArchivar(true)}
                  >
                    Archivar
                  </Boton>
                ) : null}
                {puedeEditar && !propuesta.fechaemision && (
                  <Boton
                    rojo
                    transparente
                    callback={confirmarEliminar}
                    loader={loaderEliminar}
                  >
                    Eliminar
                  </Boton>
                )}
                {puedeEditar &&
                  propuesta.fechaemision &&
                  !propuesta.fechaaceptacion &&
                  !propuesta.archivada && (
                    <Boton
                      amarillo
                      callback={confirmarRetirar}
                      loader={loaderRetirar}
                    >
                      Retirar
                    </Boton>
                  )}
                {puedeEditar && !propuesta.fechaemision && !propuesta.archivada && (
                  <Boton callback={() => setSidebarEnviar(true)}>
                    Enviar al cliente
                  </Boton>
                )}
              </>
            }
          >
            Propuesta - {propuesta.solicitud?.nombrefantasia}
          </Titulo>

          {propuesta.archivada && (
            <Card
              css={{
                marginTop: "1rem",
                backgroundColor: "var(--red-50)",
                border: "1px solid var(--red-200)"
              }}
            >
              <div
                css={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
              >
                <i
                  className="pi pi-box"
                  css={{ fontSize: "1.5rem", color: "var(--red-600)" }}
                />
                <div>
                  <div css={{ fontWeight: "bold", color: "var(--red-700)" }}>
                    Propuesta archivada
                  </div>
                  {propuesta.notasArchivada && (
                    <div css={{ color: "var(--red-600)", whiteSpace: "pre-wrap" }}>
                      {propuesta.notasArchivada}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )}

          {propuesta.fechaaceptacion && (
            <Card
              css={{
                marginTop: "1rem",
                backgroundColor: "var(--green-50)",
                border: "1px solid var(--green-200)"
              }}
            >
              <div
                css={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
              >
                <i
                  className="pi pi-check-circle"
                  css={{ fontSize: "1.5rem", color: "var(--green-600)" }}
                />
                <div>
                  <div css={{ fontWeight: "bold", color: "var(--green-700)" }}>
                    Propuesta aceptada
                  </div>
                  <div css={{ color: "var(--green-600)" }}>
                    {propuesta.nombreAceptador
                      ? `Aceptada por ${propuesta.nombreAceptador} el `
                      : "Aceptada el "}
                    {formatFecha(propuesta.fechaaceptacion)}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {propuesta.facturas?.map((factura) => {
            const pagada = !!factura.fechaPago;
            return (
              <Card
                key={factura.id}
                css={{
                  marginTop: "1rem",
                  backgroundColor: pagada ? "var(--green-50)" : "var(--yellow-50)",
                  border: `1px solid ${pagada ? "var(--green-200)" : "var(--yellow-200)"}`
                }}
              >
                <div
                  css={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
                >
                  <i
                    className={pagada ? "pi pi-check-circle" : "pi pi-clock"}
                    css={{ fontSize: "1.5rem", color: pagada ? "var(--green-600)" : "var(--yellow-600)" }}
                  />
                  <div>
                    <div css={{ fontWeight: "bold", color: pagada ? "var(--green-700)" : "var(--yellow-700)" }}>
                      {factura.moneda?.simbolo} {factura.monto} — {pagada ? "Pagada" : "Pendiente de pago"}
                    </div>
                    {pagada && (
                      <div css={{ color: "var(--green-600)" }}>
                        Pagada el {formatFecha(factura.fechaPago)}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}

          <Card css={{ marginTop: "1rem" }}>
            <Titulo>Detalles de la propuesta</Titulo>
                <div className="grid">
                  <CampoInformacion nombreCampo="Cliente">
                    {propuesta.cliente?.razonsocial || "-"}
                  </CampoInformacion>

                  <CampoInformacion nombreCampo="Solicitud">
                    {propuesta.solicitud?.nombrefantasia || "-"}
                  </CampoInformacion>

                  <CampoInformacion nombreCampo="Fecha de creación">
                    {formatFecha(propuesta.fechacreacion) || "-"}
                  </CampoInformacion>

                  <CampoInformacion nombreCampo="Fecha de emisión">
                    {formatFecha(propuesta.fechaemision) || "-"}
                  </CampoInformacion>

                  <CampoInformacion nombreCampo="Fecha de aceptación">
                    {formatFecha(propuesta.fechaaceptacion) || "-"}
                  </CampoInformacion>

                  {propuesta.archivo && (
                    <CampoInformacion nombreCampo="Archivo">
                      <ArchivoPreview
                        nombre={propuesta.archivo.nombredescarga}
                        hash={propuesta.archivo.hash}
                      />
                    </CampoInformacion>
                  )}

                  <CampoInformacion nombreCampo="Propuesta más IVA">
                    <div
                      css={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem"
                      }}
                    >
                      <InputSwitch
                        checked={masIva}
                        onChange={(e) => toggleMasIva(e.value)}
                        disabled={loaderMasIva || soloLectura}
                      />
                      {loaderMasIva && (
                        <i
                          className="pi pi-spin pi-spinner"
                          css={{ color: "var(--grisTextos)" }}
                        />
                      )}
                      <span css={{ color: "var(--grisTextos)" }}>
                        {masIva ? "Sí" : "No"}
                      </span>
                    </div>
                  </CampoInformacion>

                  {propuesta.notas && (
                    <CampoInformacion nombreCampo="Notas para el cliente" anchoCompleto>
                      <span css={{ whiteSpace: "pre-wrap" }}>
                        {propuesta.notas}
                      </span>
                    </CampoInformacion>
                  )}
                  {propuesta.notasInternas && (
                    <CampoInformacion nombreCampo="Notas internas" anchoCompleto>
                      <span css={{ whiteSpace: "pre-wrap" }}>
                        {propuesta.notasInternas}
                      </span>
                    </CampoInformacion>
                  )}
                </div>
          </Card>

          {propuesta.procesos?.length > 0 && (
            <Card css={{ marginTop: "1rem" }}>
              <Titulo>Procesos alcanzados</Titulo>
              <ul css={{ margin: 0, paddingLeft: "1.2em" }}>
                {propuesta.procesos.map((p) => (
                  <li key={p.id}>{`${p.nombre} - ${p.nombrefantasia}`}</li>
                ))}
              </ul>
            </Card>
          )}

          {(() => {
            const acts = precios.actividades || [];
            const emitida = soloLectura;

            // Agrupa por tipo de certificado, preservando orden de aparición.
            const grupos = [];
            const indicePorTipo = {};
            for (const a of acts) {
              const clave = a.certificadoTipoId ?? "sin";
              if (!(clave in indicePorTipo)) {
                indicePorTipo[clave] = grupos.length;
                grupos.push({
                  clave,
                  // Título de sección: {tipoProceso} - {tipoCertificado}. En
                  // propuestas viejas sin procesotiponombre queda solo el
                  // certificado.
                  nombre: [a.procesotiponombre, a.certificadonombre]
                    .filter(Boolean)
                    .join(" - "),
                  notas: a.certificadoNotasPropuesta,
                  // Norma, esquema y reglamentación son del tipo de
                  // certificado, no de cada actividad.
                  certificado: (precios?.certificados || []).find(
                    (c) => c.certificadoTipoId === a.certificadoTipoId
                  ),
                  actividades: []
                });
              }
              grupos[indicePorTipo[clave]].actividades.push(a);
            }

            const actividadBody = (a) => (
              <>
                {a.excluida && (
                  <i
                    className="pi pi-times"
                    css={{ color: "var(--rojo)", marginRight: "0.5rem" }}
                  />
                )}
                {a.actividadnombre}
              </>
            );
            const precioBody = (a) =>
              emitida ? (
                `${a.moneda?.simbolo || ""} ${a.precio}`
              ) : (
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem"
                  }}
                >
                  <InputNumber
                    value={a.precio}
                    disabled={a.excluida}
                    onValueChange={(e) =>
                      handlePrecioChange(
                        a.precioActividadClienteId,
                        e.value || 0
                      )
                    }
                    mode="decimal"
                    minFractionDigits={0}
                    inputStyle={{ width: "90px" }}
                  />
                  <Dropdown
                    value={a.moneda?.id || a.monedaid}
                    disabled={a.excluida}
                    onChange={(e) =>
                      handleMonedaChange(a.precioActividadClienteId, e.value)
                    }
                    options={monedas}
                    optionLabel="simbolo"
                    optionValue="value"
                    style={{ width: "85px" }}
                  />
                </div>
              );
            const frecuenciaBody = (a) =>
              emitida ? (
                frecuenciaEtiqueta(a.frecuenciaRefacturacion)
              ) : (
                <Dropdown
                  value={a.frecuenciaRefacturacion || "NINGUNA"}
                  disabled={a.excluida}
                  onChange={(e) =>
                    handleFrecuenciaChange(a.precioActividadClienteId, e.value)
                  }
                  options={FRECUENCIA_OPCIONES}
                  optionLabel="etiqueta"
                  optionValue="value"
                  style={{ width: "180px" }}
                />
              );
            const crearAutomaticamenteBody = (a) =>
              emitida ? (
                a.crearAutomaticamente ? "Sí" : "No"
              ) : (
                <Checkbox
                  checked={!!a.crearAutomaticamente}
                  disabled={a.excluida}
                  onChange={(e) =>
                    handleCrearAutomaticamenteChange(
                      a.precioActividadClienteId,
                      e.checked
                    )
                  }
                />
              );
            const primeraBody = (a) =>
              a.cantidad > 0 ? `${a.moneda?.simbolo || ""} ${a.subtotal}` : "—";
            const accionBody = (a) =>
              a.excluida ? (
                <Boton
                  texto
                  icono
                  callback={() =>
                    handleReactivarActividad(a.precioActividadClienteId)
                  }
                >
                  <i className="pi pi-replay" />
                </Boton>
              ) : (
                <Boton
                  texto
                  rojo
                  icono
                  callback={() =>
                    handleEliminarActividad(a.precioActividadClienteId)
                  }
                >
                  <i className="pi pi-trash" />
                </Boton>
              );

            return (
              <>
                {acts.length === 0 && (
                  <Card css={{ marginTop: "1rem" }}>
                    <p css={{ color: "var(--grisTextos)" }}>
                      No hay precios definidos para esta propuesta.
                    </p>
                  </Card>
                )}
                {grupos.map((g) => (
                  <Card key={g.clave} css={{ marginTop: "1rem" }}>
                    <Titulo>{g.nombre || "Actividades"}</Titulo>
                    <InfoCertificadoPropuesta
                      certificado={g.certificado}
                      notas={g.notas}
                    />
                    <h4 css={{ marginTop: 0 }}>Precios por actividad</h4>
                    <TablaFiltrable conFiltros={false}
                      // La key incluye `emitida` para forzar el remount al
                      // retirar/emitir: con dataKey estable (id de BD) PrimeReact
                      // reusa las filas y no reflejaría el cambio a editable.
                      key={`dt-${g.clave}-${emitida ? "ro" : "rw"}`}
                      value={g.actividades}
                      size="small"
                      dataKey="precioActividadClienteId"
                      nombreArchivo="precios_actividades_propuesta"
                      rowClassName={(a) =>
                        a.excluida ? "actividad-excluida" : ""
                      }
                      css={{
                        "& tr.actividad-excluida td:not(:last-child)": {
                          textDecoration: "line-through"
                        },
                        // El nombre va en gris en vez de transparente, para que
                        // la X de la izquierda se vea con su color pleno.
                        "& tr.actividad-excluida td:first-child": {
                          color: "var(--grisTextos)"
                        },
                        "& tr.actividad-excluida td:not(:first-child):not(:last-child)":
                          { opacity: 0.5 }
                      }}
                    >
                      <Column
                        header="Actividad"
                        field="actividadnombre"
                        body={actividadBody}
                      />
                      {/* Las demás columnas se ajustan a su contenido (width 1%)
                          y el resto del ancho queda para el nombre. */}
                      <Column
                        header="Precio unitario"
                        body={precioBody}
                        style={{ width: "1%", whiteSpace: "nowrap" }}
                      />
                      <Column
                        header="Facturación"
                        body={frecuenciaBody}
                        style={{ width: "1%", whiteSpace: "nowrap" }}
                      />
                      <Column
                        header="Creación automática"
                        body={crearAutomaticamenteBody}
                        style={{ width: "7rem", textAlign: "center" }}
                      />
                      <Column
                        header="Primera facturación"
                        body={primeraBody}
                        style={{ width: "1%", whiteSpace: "nowrap" }}
                      />
                      {!emitida && (
                        <Column
                          body={accionBody}
                          style={{ width: "3rem", textAlign: "center" }}
                        />
                      )}
                    </TablaFiltrable>
                  </Card>
                ))}
              </>
            );
          })()}

          <Card css={{ marginTop: "1rem", marginBottom: "2rem" }}>
            <Titulo>Adicionales y totales</Titulo>

            {adicionales.length > 0 && (
              <>
                <h4 css={{ marginTop: "1.5rem" }}>Adicionales</h4>
                <TablaFiltrable conFiltros={false}
                  key={`dt-adic-${soloLectura ? "ro" : "rw"}`}
                  value={adicionales}
                  size="small"
                  nombreArchivo="adicionales_propuesta"
                >
                  <Column header="Descripción" field="descripcion" />
                  <Column
                    header="Modifica total"
                    body={(a) => (a.modifica ? "Sí" : "No")}
                    style={{ width: "9rem" }}
                  />
                  <Column
                    header="Precio"
                    body={(a) => {
                      const label =
                        monedas.find((m) => m.value === a.monedaid)?.etiqueta ||
                        a.moneda?.nombre ||
                        "";
                      return `${a.moneda?.simbolo || ""} ${a.precio}${label ? ` (${label})` : ""}`;
                    }}
                  />
                  {!soloLectura && (
                    <Column
                      style={{ width: "3rem", textAlign: "center" }}
                      body={(a, options) => (
                        <Boton
                          texto
                          rojo
                          icono
                          callback={() => eliminarAdicional(options.rowIndex)}
                        >
                          <i className="pi pi-trash" />
                        </Boton>
                      )}
                    />
                  )}
                </TablaFiltrable>
              </>
            )}

            {!soloLectura && (
              <>
                <h4 css={{ marginTop: "1.5rem" }}>Agregar adicional</h4>
                <div
                  css={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "1rem",
                    alignItems: "center",
                    marginBottom: "1rem"
                  }}
                >
                  <InputText
                    placeholder="Descripción"
                    value={nuevoAdicionalDescripcion}
                    onChange={(e) =>
                      setNuevoAdicionalDescripcion(e.target.value)
                    }
                    style={{ flex: "1", minWidth: "200px" }}
                  />
                  <InputNumber
                    placeholder="Precio"
                    value={nuevoAdicionalPrecio}
                    onValueChange={(e) => setNuevoAdicionalPrecio(e.value)}
                    mode="decimal"
                    minFractionDigits={0}
                    inputStyle={{ width: "100px" }}
                  />
                  <Dropdown
                    placeholder="Moneda"
                    value={nuevoAdicionalMoneda}
                    onChange={(e) => setNuevoAdicionalMoneda(e.value)}
                    options={monedas}
                    optionLabel="etiqueta"
                    optionValue="value"
                    style={{ width: "180px" }}
                  />
                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem"
                    }}
                  >
                    <Checkbox
                      inputId="modificaPrecio"
                      checked={nuevoAdicionalModifica}
                      onChange={(e) => setNuevoAdicionalModifica(e.checked)}
                    />
                    <label htmlFor="modificaPrecio" css={{ cursor: "pointer" }}>
                      Modifica total
                    </label>
                  </div>
                  <Boton texto callback={agregarAdicional}>
                    Agregar
                  </Boton>
                </div>
              </>
            )}

            {(adicionales.length > 0 || !soloLectura) && <Divider />}

            <div
              css={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                flexWrap: "wrap",
                marginTop:
                  adicionales.length === 0 && soloLectura
                    ? "1rem"
                    : 0
              }}
            >
              <span css={{ fontWeight: "bold" }}>Totales:</span>
              {actualizandoPrecios && (
                <i
                  className="pi pi-spin pi-spinner"
                  css={{ color: "var(--grisTextos)" }}
                />
              )}
              {!actualizandoPrecios &&
              precios.totales &&
              precios.totales.length > 0 ? (
                <>
                  {precios.totales.map((total, index) => (
                    <span key={index}>
                      {total.moneda?.simbolo || ""} {total.total}
                      {index !== precios.totales.length - 1 && " + "}
                    </span>
                  ))}
                  {masIva && (
                    <span css={{ color: "var(--grisTextos)" }}>+ IVA</span>
                  )}
                </>
              ) : (
                !actualizandoPrecios && (
                  <span css={{ color: "var(--grisTextos)" }}>
                    Sin totales calculados
                  </span>
                )
              )}
            </div>
          </Card>
        </div>
      )}

      <Sidebar
        visible={sidebarEnviar}
        onHide={() => setSidebarEnviar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <h2>Enviar propuesta al cliente</h2>
        <p css={{ color: "var(--grisTextos)", marginBottom: "1rem" }}>
          El cliente recibirá una notificación para ver los detalles de la
          propuesta comercial.
        </p>
        <Formulario
          config={enviarPropuestaFormConfig}
          loader={enviandoPropuesta}
          key={resetForm}
        />
      </Sidebar>

      <Sidebar
        visible={sidebarArchivar}
        onHide={() => setSidebarArchivar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <h2>Archivar propuesta</h2>
        <Formulario
          config={archivarFormConfig}
          loader={loaderArchivar}
        />
      </Sidebar>
    </>
  );
}
